import { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { adminPost } from "./adminApi";
import { consoleColors, primaryButtonSx } from "./consoleTheme";
import { indiaDate } from "../../include/indiaDate";

const empty = { spin_club: "0.10", spin_store: "0.10", coupon_club: "5", coupon_store: "5", create_coupon_club: "5", create_coupon_store: "5", print_one_side: "2" };

export default function WalletCharges() {
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [settings, history] = await Promise.all([
      adminPost("WALLET_CHARGE_GET"),
      adminPost("ADMIN_WALLET_LIST", { limit: 30 }),
    ]);
    if (settings.status === "200" && settings.data?.charges) {
      const charges = settings.data.charges;
      setForm({
        spin_club: String(charges.spin_club),
        spin_store: String(charges.spin_store),
        coupon_club: String(charges.coupon_club),
        coupon_store: String(charges.coupon_store),
        create_coupon_club: String(charges.create_coupon_club ?? charges.coupon_club),
        create_coupon_store: String(charges.create_coupon_store ?? charges.coupon_store),
        print_one_side: String(charges.print_price ?? charges.print_one_side),
      });
    } else toast.error(settings?.status_message || "Could not load wallet cuts");
    if (history.status === "200" && Array.isArray(history.data)) setRows(history.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const result = await adminPost("WALLET_CHARGE_SAVE", form);
      if (result.status === "200") {
        const charges = result.data?.charges || {};
        setForm({
          spin_club: String(charges.spin_club ?? form.spin_club),
          spin_store: String(charges.spin_store ?? form.spin_store),
          coupon_club: String(charges.coupon_club ?? form.coupon_club),
          coupon_store: String(charges.coupon_store ?? form.coupon_store),
          create_coupon_club: String(charges.create_coupon_club ?? form.create_coupon_club),
          create_coupon_store: String(charges.create_coupon_store ?? form.create_coupon_store),
          print_one_side: String(charges.print_price ?? charges.print_one_side ?? form.print_one_side),
        });
        toast.success(result.data?.message || "Wallet cuts saved");
      } else toast.error(result?.data?.error || result?.status_message || "Could not save wallet cuts");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, helper) => (
    <TextField
      label={label}
      type="number"
      value={form[key]}
      onChange={(event) => setForm({ ...form, [key]: event.target.value })}
      helperText={helper}
      inputProps={{ min: 0, step: "0.01" }}
    />
  );

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2, maxWidth: 980 }}>
      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 }, display: "grid", gap: 1.5 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 750 }}>Wallet cuts</Typography>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13.5 }}>
          These amounts are cut from the store wallet. A store in a club uses the club amount. A store that is not in a club uses the individual amount. A coupon uses the club amount only when that draw belongs to a club.
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 750 }}>Per spin</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.4 }}>
          {field("spin_club", "Club store (₹)", "Default ₹0.10 per spin")}
          {field("spin_store", "Individual store (₹)", "Default ₹0.10 per spin")}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 750 }}>When coupons are created</Typography>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13 }}>
          Lucky Draw Coupons: cut once when the draw is created. Store coupons: cut this amount times the number of coupons.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.4 }}>
          {field("create_coupon_club", "Club (₹)", "Default ₹5")}
          {field("create_coupon_store", "Individual store (₹)", "Default ₹5")}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 750 }}>Per Lucky Draw Coupon issued</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.4 }}>
          {field("coupon_club", "Club draw (₹)", "Default ₹5 per coupon scanned")}
          {field("coupon_store", "Individual draw (₹)", "Default ₹5 per coupon scanned")}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 750 }}>Per printed loyalty card</Typography>
        <Typography sx={{ color: consoleColors.muted, fontSize: 13 }}>
          Cut from the store or club wallet when a loyalty-card print request is submitted.
        </Typography>
        {field("print_one_side", "Per card (₹)", "Default ₹2 per card")}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" sx={primaryButtonSx} disabled={saving} onClick={save}>{saving ? "Saving..." : "Save"}</Button>
        </Box>
      </Box>

      <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: { xs: 2, md: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, mb: 1.2, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 750 }}>Transaction history</Typography>
          <Button sx={{ textTransform: "none" }} onClick={() => navigate("/console/wallet")}>Open full wallet list</Button>
        </Box>
        {rows.length === 0 && <Typography sx={{ color: consoleColors.muted, fontSize: 14 }}>No wallet transactions yet.</Typography>}
        <Box sx={{ display: "grid", gap: 0.8 }}>
          {rows.map((row) => (
            <Box key={row.row_temp_id || `${row.store_id}-${row.date_added}-${row.comment}`} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.7fr 0.7fr 1.6fr 0.9fr" }, gap: 1, borderTop: `1px solid ${consoleColors.line}`, py: 0.8 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 650 }}>{row.store_name || row.store_id}</Typography>
              <Typography sx={{ fontSize: 14 }}>{row.wallet_action === "Sub" ? "Cut" : "Add"}</Typography>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>₹{Number(row.wallet_amount || 0).toFixed(2)}</Typography>
              <Typography sx={{ fontSize: 14, color: consoleColors.muted }}>{row.comment || "—"}</Typography>
              <Typography sx={{ fontSize: 14 }}>{indiaDate(row.date_added)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
